/**
 * Utility functions for aspect ratio calculations
 */

/**
 * Returns the reduced aspect ratio as a "W:H" string.
 * @param {number} width - The width dimension
 * @param {number} height - The height dimension
 * @returns {string} - The aspect ratio in "W:H" format, or "unknown" if invalid input
 * @throws {Error} - If width or height are invalid
 */
export const calculateAspectRatio = (width, height) => {
  // Validate inputs
  if (typeof width !== 'number' || typeof height !== 'number') {
    console.error('[aspectRatio] Invalid input types:', { width, height, widthType: typeof width, heightType: typeof height });
    return 'unknown';
  }
  
  if (!isFinite(width) || !isFinite(height)) {
    console.error('[aspectRatio] Non-finite values:', { width, height });
    return 'unknown';
  }
  
  if (width <= 0 || height <= 0) {
    console.error('[aspectRatio] Non-positive dimensions:', { width, height });
    return 'unknown';
  }
  
  try {
    let a = width,
      b = height;
    while (b) {
      [a, b] = [b, a % b];
    }
    
    const ratioW = width / a;
    const ratioH = height / a;
    
    // Validate result
    if (!isFinite(ratioW) || !isFinite(ratioH)) {
      console.error('[aspectRatio] Calculation resulted in non-finite ratio:', { ratioW, ratioH, width, height });
      return 'unknown';
    }
    
    return `${ratioW}:${ratioH}`;
  } catch (error) {
    console.error('[aspectRatio] Error calculating aspect ratio:', error, { width, height });
    return 'unknown';
  }
};

/**
 * Calculate the width (in pixels) for a given aspect ratio and height,
 * rounded to one decimal place.
 *
 * @param {string} aspect - The aspect ratio as "W:H" (e.g. "4:3").
 * @param {number} heightPx - The height in pixels.
 * @returns {number|null} - The calculated width in pixels, rounded to 2 decimals, or null if invalid
 */
export const calculateWidth = (aspect, heightPx) => {
  // Validate aspect ratio format
  if (typeof aspect !== 'string' || !aspect.includes(':')) {
    console.error('[aspectRatio] Invalid aspect ratio format:', { aspect });
    return null;
  }
  
  // Validate height
  if (typeof heightPx !== 'number' || !isFinite(heightPx) || heightPx <= 0) {
    console.error('[aspectRatio] Invalid height:', { heightPx });
    return null;
  }
  
  try {
    const parts = aspect.split(':');
    if (parts.length !== 2) {
      console.error('[aspectRatio] Aspect ratio must have exactly 2 parts:', { aspect, parts });
      return null;
    }
    
    const [wRatio, hRatio] = parts.map(Number);
    
    // Validate parsed ratios
    if (!isFinite(wRatio) || !isFinite(hRatio) || wRatio <= 0 || hRatio <= 0) {
      console.error('[aspectRatio] Invalid ratio values:', { wRatio, hRatio, aspect });
      return null;
    }
    
    const rawWidth = (wRatio / hRatio) * heightPx;
    
    // Validate result
    if (!isFinite(rawWidth) || rawWidth <= 0) {
      console.error('[aspectRatio] Invalid calculated width:', { rawWidth, wRatio, hRatio, heightPx });
      return null;
    }
    
    return parseFloat(rawWidth.toFixed(2));
  } catch (error) {
    console.error('[aspectRatio] Error calculating width:', error, { aspect, heightPx });
    return null;
  }
};
